import { YearsInBusiness } from './credit';
import { UserRoleIds } from './user-roles';

export enum LoadingStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  SUCCEEDED = 'SUCCEEDED',
  UNSET = 'UNSET',
}

export enum JobsiteUpdateOperationType {
  ADD = 'ADD',
  EDIT = 'EDIT',
  REMOVE = 'REMOVE',
  SELECT = 'SELECT',
}

export enum ManageTeamOperationType {
  ADD = 'ADD',
  EDIT = 'EDIT',
  REMOVE = 'REMOVE',
}

export enum ZipCodeType {
  STANDARD = 'STANDARD',
  EXTENDED = 'EXTENDED',
}

export enum MessageTypes {
  SMS = 'sms',
  EMAIL = 'email',
}

export enum MessagePersonalDetails {
  EMAIL = 'email',
  PHONE = 'phone',
}

export enum NotificationTypes {
  SMS = 'sms',
  EMAIL = 'email',
}

export type Address = {
  street1: string | null;
  city: string | null;
  state: string | null;
  zip: string | number | null;
};

export type PersonalDetailsType = {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
};

export enum RoleToSecurityLevelId {
  ADMIN = 1,
  MANAGER = 2,
  PENDING = 3,
  SALESPERSON = 4,
  USER = 5,
  OPERATIONS = 6,
  DRIVER = 7,
}

export type CompanyUser = {
  user_id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: RoleToSecurityLevelId;
};

export type CompanyDetailsType = {
  name: string | null;
  address: Address | null;
  phone: string | null;
  billingContact: {
    name: string | null;
    cell: string | null;
    address: Address | null;
    email: string | null;
  };
  billingLocationId: number | null;
  ownerId: number;
  users: UserDetails[];
  pendingUsers: UserInvitation[];
};

export type CompanyInfoDetails = {
  name: string | null;
  address: Address | null;
  phone: string | null;
  billingLocationId: number | null;
  ownerId: number;
};

export type BillingContactType = {
  name: string | null;
  cell: string | null;
  address: Address | null;
  email: string | null;
};

export type BillingInfoDetails = {
  billingContact: BillingContactType;
  billingLocationId: number | null;
};

export type NotificationSettingsType = {
  email: boolean | null;
  sms: boolean | null;
};

export type JobsiteInfoDetailsType = CompanyLocation[];
export type NotificationInfoDetailsType = Notification[];

export type ProfileState = {
  billingDetails: { data: BillingInfoDetails | null; loadingStatus: LoadingStatus };
  companyDetails: { data: CompanyInfoDetails | null; loadingStatus: LoadingStatus };
  jobsiteDetails: {
    data: JobsiteInfoDetailsType | null;
    loadingStatus: LoadingStatus;
    hasData: boolean;
    allDataFetched: boolean;
  };
  teamDetails: { data: ManageTeamInfoDetails | null; loadingStatus: LoadingStatus };
  notificationDetails: { data: NotificationInfoDetailsType | null; loadingStatus: LoadingStatus };
  messageSettings: {
    data: MessageTypeWithChannels[] | null;
    loadingStatus: LoadingStatus;
  };
  personalDetails: {
    data: PersonalDetailsType | null;
    loadingStatus: LoadingStatus;
    isUpdateModalOpen: boolean;
  };
};

export type SetPersonalDetailsPayload = {
  name: string | null;
  phone: string | null;
  email: string | null;
};

export interface UpdatePersonalDetails {
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
}

export interface UpdateCompanyDetails {
  billing_location_id?: string | number | null;
  home_office_location_id?: number;
  name?: string;
  doing_business_as?: string | null;
  business_nature?: string | null;
  org_state?: number | null;
  years_in_business?: YearsInBusiness | null;
}

export interface CreateUpdateCompanyUser {
  employee_id?: string | null;
  user_id?: number;
  first_name?: string | null;
  last_name?: string | null;
  email_address?: string | null;
  phone_number?: string | null;
  cell_phone_number?: string | null;
  read_only?: boolean | null;
  timezone?: string | null;
  can_grant_permissions?: boolean | null;
  can_create_asset_financial_records?: boolean | null;
  can_read_asset_financial_records?: boolean | null;
  can_access_camera?: boolean | null;
  can_rent?: boolean | null;
  security_level_id?: number | null;
}

export interface CreateUpdateCompanyAddress {
  nickname?: string | null;
  street_1?: string | null;
  street_2?: string | null;
  city?: string | null;
  state_id?: number | null;
  zip_code?: string | number | null;
  zip_code_extended?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  jobsite?: boolean;
}

export interface UpdateUserNotificationDetails {
  notification_type_ids: number[];
}

export interface PersonalDetails {
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email_address: string;
  phone_number: string | null;
}

export interface UpdateCompanyDetails {
  billing_location_id?: string | number | null;
}

export interface Location {
  location_id: number;
  location_type_id: number;
  nickname: string;
  description: string | null;
  street_1: string | null;
  street_2: string | null;
  city: string;
  zip_code: string | number;
  latitude: number | null;
  longitude: number | null;
  user_id: number;
  company_id: number;
  state: {
    state_id: number;
    abbreviation: string;
    name: string;
  };
  needs_review: boolean;
  universal_entity_id: number;
}

export interface User {
  user_id: number;
  company_id: number;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  employee_id: string;
  deleted: boolean;
  email_address: string;
  phone_number: string;
  date_created: string;
  xero_salesperson_account_code: string | null;
  zip_code: number;
  branch_id: number;
  keypad_code: string | null;
  security_level_id: number;
  read_only: boolean;
  preferred_landing_page: string | null;
  cell_phone_number: string | null;
  sms_opted_out: boolean;
  can_grant_permissions: boolean | null;
  can_create_asset_financial_records: boolean;
  can_read_asset_financial_records: boolean;
  can_access_camera: boolean;
  is_federated: boolean | null;
  is_authorized_signer: boolean;
}

export interface State {
  state_id: number;
  abbreviation: string;
  name: string;
}

export interface BillingProvider {
  billing_provider_id: number;
  name: string;
  remit_location_id: number;
  remit_location: Location;
}

export interface NetTerms {
  net_terms_id: number;
  name: string;
}

export interface WhiteLabelApplication {
  white_label_application_id: number;
  name: string;
  show_rentals: boolean;
  show_dashboard: boolean;
  show_rental_contracts: boolean;
  show_rental_invoices: boolean;
  allow_off_renting: boolean;
  allow_location_sharing: boolean;
  allow_rental_requests: boolean;
  customer_url: string;
}

export interface HoursOfServiceType {
  hours_of_service_type_id: number;
  name: string;
  display_name: string;
}

export interface Company {
  doing_business_as?: string | null;
  business_nature?: string | null;
  org_state?: number | null;
}

export interface CompanyContract {
  company: Company;
}

export interface CompanyDetails {
  company_id: number;
  crm_entity_id: number | null;
  ou_id: string;
  name: string;
  xero_id: number | null;
  do_not_rent: boolean;
  is_eligible_for_payouts: boolean;
  owner_user_id: number;
  net_terms_id: number;
  external_billing_provider_id: number | null;
  billing_provider_id: number;
  delivery_vendor: boolean;
  service_vendor: boolean;
  supply_vendor: boolean;
  billing_location: Location | null;
  photo: string | null;
  net_terms: NetTerms;
  billing_provider: BillingProvider;
  credit_limit: number | null;
  timezone: string;
  i18n_locale: string;
  i18n_unit: string;
  i18n_temperature: string;
  white_label_application_id: number;
  white_label_application: WhiteLabelApplication;
  dot_number: string;
  hours_of_service_type_id: number;
  hours_of_service_type: HoursOfServiceType;
  home_office_location_id: number;
  home_office_location: Location;
  has_fleet: boolean;
  has_rentals: boolean;
  employer_identification_number: string | null;
  has_fleet_cam: boolean;
  authorized_signer_id: number;
  current_company_contract: CompanyContract;
  has_msa: undefined;
  authorized_signer: User;
  doing_business_as: string | null;
  business_nature: string | null;
}

export interface CompanyBillingPreferences {
  companyId: number;
  isPaperlessBilling: boolean;
  disableMonthlyStatements: boolean;
  rentalBillingCycleStrategy: string;
  primaryBillingContactUserId: number;
  cycleBillingOnly: boolean;
  legalAudit: boolean;
  legalAuditTime: string | null;
  internalCompany: boolean;
  monthlyStatementContacts: number[];
  invoiceContacts: number[];
  managedBilling: boolean;
  badDebt: boolean;
  inBankruptcy: boolean;
}

export interface CompanyUsers {
  users: UserDetails[];
  pendingUsers: UserInvitation[];
}

export interface ManageTeamInfoDetails extends CompanyUsers {}

export enum CompanyUserState {
  EXISTING = 'EXISTING',
  PENDING = 'PENDING',
}

export interface UpdateUserInvitationPayload {
  security_level_id: UserRoleIds;
}

export interface UserInvitation {
  company_id: number;
  email: string;
  security_level_id: number;
}

export interface UserDetails {
  user_id: number;
  company_id: number;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  employee_id: string;
  deleted: boolean;
  email_address: string;
  phone_number: string;
  date_created: string;
  xero_salesperson_account_code: string | null;
  zip_code: number | null;
  branch_id: number;
  keypad_code: string | null;
  security_level_id: number;
  read_only: boolean;
  preferred_landing_page: string | null;
  cell_phone_number: string | null;
  sms_opted_out: boolean;
  can_grant_permissions: boolean;
  can_create_asset_financial_records: boolean;
  can_read_asset_financial_records: boolean;
  can_access_camera: boolean;
  is_federated: boolean;
  is_authorized_signer: boolean;
  company: CompanyDetails;
  user_type_id: number;
  location: Location | null;
  timezone: string;
  can_rent: boolean;
  branch: {
    id: number;
    branch_id: number;
    account_rep_user_id: number | null;
    name: string;
    description: string | null;
    phone_number: string | null;
    state_id: number | null;
    active: boolean;
    location_id: number;
    company_id: number;
    is_public_msp: boolean;
    abbreviation: string;
    default_branch: boolean;
    service_email: string | null;
    sales_email: string | null;
  };
}

export interface CompanyLocation extends Location {
  state_id: number;
  jobsite: boolean;
  formatted_address: string;
}

export interface Notification {
  notification_type_id: number;
  user_id: number;
  user_notification_preference_id: number;
}

export interface NotificationType {
  name: string;
  notification_type_id: number;
}

export enum MessageTypeGroups {
  MARKETING_NOTIFICATIONS = 'MARKETING_NOTIFICATIONS',
}

export interface MessageChannel {
  channel_id: number;
  channel_name: MessageTypes;
}

export interface MessageTypeWithChannels {
  message_type_id: number;
  message_type_name: MessageTypeGroups;
  message_type_description?: string;
  loadingStatus?: LoadingStatus;
  channels: {
    channel_id: number;
    channel_name: MessageTypes;
    is_active: boolean;
  }[];
}

export interface UpdateMessageSettings {
  message_type_id: number;
  message_type_name: MessageTypeGroups;
  channels: {
    channel_id: number;
    channel_name: MessageTypes;
    is_active: boolean;
  }[];
}

export interface MessageTypeLoadingStatusPayload {
  message_type_id: number;
  loadingStatus: LoadingStatus;
}

export interface MessageTypeChannelsUpdatePayload {
  message_type_id: number;
  active_channels: {
    channel_id: number;
    channel_name: MessageTypes;
  }[];
}

export interface MessageChannelConfig {
  messageChannelId: number;
  messageChannelType: MessageTypes;
  labelKey: string;
  statusKeys: {
    on: string;
    off: string;
  };
  modalLabelKey: string;
  primaryLabel: string;
  showPersonalDetail: boolean;
  personalDetailKey?: MessagePersonalDetails;
}

export interface MessageTypeConfig {
  messageTypeId: number;
  messageTypeGroup: MessageTypeGroups;
  labelKey: string;
  modalLabelKey: string;
}

export interface State {
  abbreviation: string;
  name: string;
  state_id: number;
}

export type JobsiteLocationLoadingStatusType = {
  location_id: number;
  status: LoadingStatus;
};

export type CompanyUserLoadingStatusType = {
  user_id: number;
  status: LoadingStatus;
  email?: string;
};

export enum MyProfileViewState {
  HOME = 'HOME',
  PERSONAL_INFO = 'PERSONAL_INFO',
  COMPANY_INFO = 'COMPANY_INFO',
  BILLING_INFO = 'BILLING_INFO',
  MANAGE_TEAM = 'MANAGE_TEAM',
  MANAGE_JOBSITES = 'MANAGE_JOBSITES',
  ADD_JOBSITE = 'ADD_JOBSITE',
  ADD_TEAM_MEMBER = 'ADD_TEAM_MEMBER',
  NOTIFICATIONS = 'NOTIFICATIONS',
}

export enum PersonalInfoUpdateFormFieldType {
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  MOBILE_NUMBER = 'MOBILE_NUMBER',
  EMAIL = 'EMAIL',
}

export enum CompanyInfoUpdateFormFieldType {
  COMPANY_NAME = 'COMPANY_NAME',
  COUNTRY = 'COUNTRY',
  COMPANY_ADDRESS = 'COMPANY_ADDRESS',
  CITY = 'CITY',
  STATE = 'STATE',
  ZIP = 'ZIP',
  COMPANY_PHONE = 'COMPANY_PHONE',
}

export enum BillingInfoUpdateFormFieldType {
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  PHONE = 'PHONE',
  BILLING_ADDRESS = 'BILLING_ADDRESS',
  CITY = 'CITY',
  STATE = 'STATE',
  ZIP = 'ZIP',
  EMAIL = 'EMAIL',
}

export enum JobsiteInfoUpdateFormFieldType {
  JOBSITE_NAME = 'JOBSITE_NAME',
  ADDRESS = 'ADDRESS',
  CITY = 'CITY',
  STATE = 'STATE',
  ZIP = 'ZIP',
}

export enum PersonalInfoUpdateFormSectionType {
  ACCOUNT_HOLDER = 'ACCOUNT_HOLDER',
  MOBILE_NUMBER = 'MOBILE_NUMBER',
  EMAIL = 'EMAIL',
}

export enum CompanyInfoUpdateFormSectionType {
  COMPANY_NAME = 'COMPANY_NAME',
  COMPANY_ADDRESS = 'COMPANY_ADDRESS',
  COMPANY_PHONE = 'COMPANY_PHONE',
}

export enum BillingInfoUpdateFormSectionType {
  BILLING_POINT_OF_CONTACT = 'BILLING_POINT_OF_CONTACT',
  BILLING_PHONE = 'BILLING_PHONE',
  BILLING_ADDRESS = 'BILLING_ADDRESS',
  BILLING_EMAIL = 'BILLING_EMAIL',
}

export enum JobsiteInfoUpdateFormSectionType {
  ADD = 'ADD',
  EDIT = 'EDIT',
}

export enum InputFieldComponentType {
  TEXTFIELD = 'TEXTFIELD',
  SELECT = 'SELECT',
  EXPANDABLE_FORM = 'EXPANDABLE_FORM',
  ADDRESS = 'ADDRESS',
}

export type AddressFormFieldKey = 'address' | 'latitudeLongitude' | 'city' | 'state' | 'zip';

export type PersonalInfoUpdateFormFieldKey = 'firstName' | 'lastName' | 'mobileNumber' | 'email';

export type CompanyInfoUpdateFormFieldKey = 'companyName' | 'country' | 'companyPhone';

export type BillingInfoUpdateFormFieldKey = 'firstName' | 'lastName' | 'phone' | 'email';

export type JobsiteInfoUpdateFormFieldKey = 'jobsiteName';

export type ManageTeamUpdateFormFieldKey = 'firstName' | 'lastName' | 'email' | 'role';

export type UpdateFormFieldType =
  | PersonalInfoUpdateFormFieldType
  | CompanyInfoUpdateFormFieldType
  | BillingInfoUpdateFormFieldType
  | JobsiteInfoUpdateFormFieldType;
export type UpdateFormFieldKey =
  | AddressFormFieldKey
  | PersonalInfoUpdateFormFieldKey
  | CompanyInfoUpdateFormFieldKey
  | BillingInfoUpdateFormFieldKey
  | JobsiteInfoUpdateFormFieldKey;

export interface FieldDetailNormal {
  fieldType: UpdateFormFieldType;
  fieldKey: UpdateFormFieldKey;
  fieldLabelId: string;
  fieldSecondaryLabelId?: string;
  inputComponentType: InputFieldComponentType;
  inputType: React.HTMLInputTypeAttribute | undefined;
  inputMode:
    | 'search'
    | 'text'
    | 'none'
    | 'email'
    | 'tel'
    | 'url'
    | 'numeric'
    | 'decimal'
    | undefined;
  selectOptions?: {
    value: string;
    label: string;
  }[];
  defaultSelectOption?: string;
  required: boolean;
  visible: boolean;
  disabled: boolean;
  onSubLabelClick?: () => void;
}

export interface AddressFieldDetails {
  fieldLabelId: string;
  disabled?: boolean;
}

export interface FieldDetailAddress {
  inputComponentType: InputFieldComponentType.ADDRESS;
  addressFieldLabelId: string;
  coordinatesFieldLabelId?: string;
  cityFieldLabelId: string;
  stateFieldLabelId: string;
  zipFieldLabelId: string;
  allowCoordinatesInput: boolean;
  config: AddressInputFormConfig;
}

export type FieldDetail = FieldDetailNormal | FieldDetailAddress;

export enum AddressInputFormType {
  DEFAULT = 'DEFAULT',
  EXPANDABLE = 'EXPANDABLE',
}

export interface DefaultAddressInputFormConfig {
  type: AddressInputFormType;
}

export interface ExpandableAddressInputFormConfig {
  type: AddressInputFormType;
  formLabelId: string;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  fieldValue: string;
}

export type AddressInputFormConfig =
  | DefaultAddressInputFormConfig
  | ExpandableAddressInputFormConfig;

export type Range = {
  min: number;
  max: number;
};

export interface ProfileDataLoadPayload {
  refetch: boolean;
  page?: number | null;
  size?: number | null;
}

export interface JobsitesDataLoadPayload extends ProfileDataLoadPayload {}

export interface AddJobsiteByIdPayload {
  jobsiteLocationId: number;
}
export interface AddJobsitePayload {
  jobsiteLocation: CompanyLocation;
  preferredJobsiteLocationId: number | null | undefined;
}

export interface UpdateJobsiteDataStatusPayload {
  pageSize: number | undefined | null;
  newJobsites: CompanyLocation[];
}

export interface UpdateJobsiteLocationListArguments {
  jobsiteLocation: CompanyLocation | undefined | null;
  locationId: number;
  operationType: JobsiteUpdateOperationType;
  preferredJobsiteLocationId: number | null | undefined;
  stateObject?: State | null;
  requestBody?: CreateUpdateCompanyAddress | null;
}
